import {
  DevicePullUpTask,
  PullUpDeviceState,
  MANUAL_CONFIRMATION_TIMEOUT_IN_MS,
  DEVICE_STATUS_CHANGE_TIMEOUT_IN_MS,
  PullUpDeviceType,
} from './DevicePullUpTask'
import type { PullUpDeviceListener } from './DevicePullUpTask'
import { InvalidedParameterException } from '../Exceptions'
import { isString } from 'lodash'
import { isEmpty } from '../Utils'

interface InternalGuideManager {
  // TODO:js code
  pullUpDevice(
    deviceId: string,
    listener?: PullUpDeviceListener,
    isShowConnectModal?: boolean,
    isShowTips?: boolean
  ): unknown
}

export class DevicePullUpTaskManager {
  #devicePullTaskMap = new Map<string, DevicePullUpTask>()
  #devicePullPasswordTaskMap = new Map<string, DevicePullUpTask>()
  #devicePullManualTaskMap = new Map<string, DevicePullUpTask>()
  #currentDeviceId = ''
  get internalInstance(): InternalGuideManager {
    return {
      pullUpDevice: (
        deviceId: string,
        listener?: PullUpDeviceListener,
        isShowConnectModal = false,
        isShowTips = false
      ) =>
        this.pullUpDevice(
          {
            deviceId,
          },
          listener,
          isShowConnectModal,
          isShowTips
        ),
    }
  }

  resetTask(deviceId: string) {
    this.#devicePullTaskMap.get(deviceId)?.reset()
    this.#devicePullPasswordTaskMap.get(deviceId)?.reset()
    this.#devicePullManualTaskMap.get(deviceId)?.reset()
  }

  #getDevicePullUpTaskMap(type?: PullUpDeviceType) {
    switch (type) {
      case PullUpDeviceType.BY_MANUAL_CONFIRM:
        return this.#devicePullManualTaskMap
      case PullUpDeviceType.BY_PASSWORD:
        return this.#devicePullPasswordTaskMap
      default:
        return this.#devicePullTaskMap
    }
  }

  #getDevicePullUpTaskInfo(deviceId: string, type?: PullUpDeviceType) {
    const taskMap = this.#getDevicePullUpTaskMap(type)
    let devicePullUpTask = taskMap.get(deviceId)

    devicePullUpTask =
      devicePullUpTask && devicePullUpTask.type === type
        ? devicePullUpTask
        : taskMap.set(deviceId, this.#createDevicePullUpTask(deviceId, type)).get(deviceId)

    return { devicePullUpTask: devicePullUpTask!, taskMap }
  }

  #createDevicePullUpTask(id: string, type?: PullUpDeviceType) {
    const waitTime =
      type === PullUpDeviceType.BY_MANUAL_CONFIRM
        ? MANUAL_CONFIRMATION_TIMEOUT_IN_MS
        : DEVICE_STATUS_CHANGE_TIMEOUT_IN_MS

    return new DevicePullUpTask(id, waitTime, type)
  }

  #addPullDeviceTask(deviceId: string, type?: PullUpDeviceType) {
    const { devicePullUpTask, taskMap } = this.#getDevicePullUpTaskInfo(deviceId, type)

    devicePullUpTask.addListener({
      onPullSuccess: (deviceId: string) => {
        taskMap.delete(deviceId)
      },
      onPullTimeout: (deviceId: string) => {
        taskMap.delete(deviceId)
      },
      onPullFailed: (deviceId: string, state) => {
        if (devicePullUpTask.type !== undefined && state === PullUpDeviceState.ERR_NOT_IN_CONNECT_TIME) return
        taskMap.delete(deviceId)
      },
    })

    return devicePullUpTask
  }

  async pullUpDevice(
    params: {
      deviceId: unknown
      pushPassword?: unknown
      type?: PullUpDeviceType
    },
    listener?: PullUpDeviceListener,
    isShowTips?: boolean,
    isShowConnectModal?: boolean,
    isCheckConfig?: boolean
  ) {
    //TODO:把appid作为入参

    let pushPassword

    if (!isString(params.deviceId) || isEmpty(params.deviceId)) {
      return Promise.reject(new InvalidedParameterException('deviceId必须是非空string类型'))
    }

    if (params.pushPassword) {
      if (isString(params.pushPassword)) {
        pushPassword = params.pushPassword
      } else {
        return Promise.reject(new InvalidedParameterException('pushPassword必须是string类型'))
      }
    }

    const devicePullUpTask = this.#addPullDeviceTask(params.deviceId, params.type)

    if (this.#currentDeviceId !== params.deviceId) {
      // 设备切换会重置前一个设备的拉取任务
      this.resetTask(this.#currentDeviceId)
      this.#currentDeviceId = params.deviceId
    }
    devicePullUpTask.start(
      {
        deviceId: params.deviceId,
        pushPassword,
        type: params.type,
      },
      listener,
      isShowTips,
      isShowConnectModal,
      isCheckConfig
    )
  }
}

const INSTANCE = new DevicePullUpTaskManager()

export { INSTANCE }
